import PropTypes from 'prop-types';
import _ from 'underscore';
import URI from 'urijs';
import classnames from 'classnames';
import React, { memo, useCallback, useContext, useLayoutEffect, useState, useMemo, useRef } from 'react';

import { CheckBankTransferForm } from '@bingads-webui-cc-react/check-bank-transfer';
import { Adder } from '@bingads-webui-cc-react/pidl-payment-method-actions-base';
import AjaxPromise from '@bingads-webui-clientcenter/ajax-promise-smart';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import MsaAuth from '@bingads-webui-clientcenter/msa-auth-smart';
import PidlService from '@bingads-webui-clientcenter/pidl-service-smart';
import PifdAuth from '@bingads-webui-clientcenter/pifd-auth-smart';
import * as paymentAsAServiceApi from '@bingads-webui-campaign-react/payment-as-a-service-api';
import IWindow from '@bingads-webui-clientcenter/window-interface';
import { ValidationErrorContext } from '@bingads-webui-cc-react/validation-error-context';
import { PaymentContext } from '@bingads-webui-cc-react/payment-context';
import { addressPropType, i18nPropType, PropTypeBaseAddPIForm } from '../shapes';

const FieldId = 'PreferredBillToPaymentInstrumentId';

const getPidlPiType = piType => _.findKey(Constants.PaymentInstrumentType, v => v === piType);

const getAntiForgeryToken = () => {
  const antiForgeryTokenInput = IWindow.document.querySelector('input[name="__RequestVerificationToken"]');
  return antiForgeryTokenInput && antiForgeryTokenInput.value;
};

const usePidlSubmittingFunction = (setPidlIsValid, setLoading, loading, isValid = null) =>
  useCallback(
    () => {
      setPidlIsValid(_.isNull(isValid) ? !loading : isValid);
      setLoading(loading);
    },
    [isValid, loading, setPidlIsValid, setLoading]
  );

const useEventHandlers = (handlePiAddSuccess, setPidlIsValid, clearPiType, setLoading, deleteCtpPi, i18n, setPidlLoading) => {
  const { setValidationErrors } = useContext(ValidationErrorContext);
  const handleTerminatingError = useCallback(
    ({ errorMessage } = {}) => {
      setPidlIsValid(false);
      clearPiType();
      setLoading(false);
      if (errorMessage) {
        setValidationErrors([{
          FieldId,
          DangerousHtmlMessage: errorMessage,
        }]);
      } else {
        setValidationErrors([{
          FieldId,
          Message: i18n.getString(_TL_('An unknown error occured while attempting to add this payment method. Please try again')),
        }]);
      }
    },
    [i18n, setValidationErrors, setPidlIsValid, clearPiType, setLoading]
  );
  const handleScaFailOrClose = useCallback(
    (pi) => {
      deleteCtpPi(pi);
      setPidlIsValid(false);
      clearPiType();
      setLoading(false);
    },
    [deleteCtpPi, setPidlIsValid, clearPiType, setLoading]
  );
  const handleScaChallengeFailure = useCallback(
    (pi) => {
      handleScaFailOrClose(pi);
      setValidationErrors([{
        FieldId,
        Message: i18n.getString(_TL_('We\'re sorry, but we\'ve encountered a problem with your card authentication. Please try again. If that doesn\'t fix the problem, contact your bank for assistance.')),
      }]);
    },
    [handleScaFailOrClose, i18n, setValidationErrors]
  );
  const handleScaTerminatingError = useCallback(
    (pi, { errorMessage } = {}) => {
      handleScaFailOrClose(pi);
      if (errorMessage) {
        setValidationErrors([{
          FieldId,
          DangerousHtmlMessage: errorMessage,
        }]);
      } else {
        setValidationErrors([{
          FieldId,
          Message: i18n.getString(_TL_('We\'ve encountered an error and can\'t continue. Please contact support for further assistance.')),
        }]);
      }
    },
    [handleScaFailOrClose, i18n, setValidationErrors]
  );

  const handleProcessingStart = usePidlSubmittingFunction(setPidlIsValid, setLoading, true);
  const handlePidlDocRendered = usePidlSubmittingFunction(setPidlIsValid, setLoading, false);
  const handleProcessingComplete = usePidlSubmittingFunction(setPidlIsValid, setLoading, false);
  const handleScaProcessingStart = usePidlSubmittingFunction(setPidlIsValid, setLoading, true);
  const handleScaPidlDocRendered = usePidlSubmittingFunction(setPidlIsValid, setLoading, false, false);
  const handleScaProcessingComplete = usePidlSubmittingFunction(setPidlIsValid, setLoading, false, false);
  const handleAddOrUpdateSuccess = useCallback(({ mtPiId }) => handlePiAddSuccess(mtPiId), [handlePiAddSuccess]);
  const handlePidlLoadingStart = useCallback(() => setPidlLoading(true), [setPidlLoading]);
  const handlePidlLoadingComplete = useCallback(() => setPidlLoading(false), [setPidlLoading]);

  return useMemo(
    () => ({
      handleTerminatingError,
      handleProcessingStart,
      handlePidlDocRendered,
      handleProcessingComplete,
      handleGoHome: _.noop,
      handleScaChallengeSuccess: _.noop,
      handleScaChallengeFailure,
      handleScaTerminatingError,
      handleScaProcessingStart,
      handleScaPidlDocRendered,
      handleScaProcessingComplete,
      handleScaGoHome: handleScaFailOrClose,
      handleAddOrUpdateSuccess,
      handlePidlLoadingStart,
      handlePidlLoadingComplete,
    }),
    [
      handleTerminatingError,
      handleProcessingStart,
      handlePidlDocRendered,
      handleProcessingComplete,
      handleScaFailOrClose,
      handleScaChallengeFailure,
      handleScaTerminatingError,
      handleScaProcessingStart,
      handleScaPidlDocRendered,
      handleScaProcessingComplete,
      handleAddOrUpdateSuccess,
      handlePidlLoadingStart,
      handlePidlLoadingComplete,
    ]
  );
};

const RenderAddPIFormUnMemo = ({
  cid,
  pidlAdderClassName,
  currency,
  setPidlIsValid,
  clearPiType,
  country,
  i18n,
  address,
  onOfflinePiFormValidate,
  offlinePiCountryList,
  piType,
  shouldRetryWithPopup,
  shouldAuthenticateWithPopup,
  paymentAdderOptions: {
    ccBaseUrl,
    restfulService,
    returnUrl,
    attachSubmitEvent,
    detachSubmitEvent,
    findStates,
    isPsd2ValidationEnabled,
    handlePiAddSuccess,
    culture,
    setLoading,
    getAdditionalSuccessParams,
    pidlLoggerCreate,
    setPidlLoading = _.noop,
  },
}) => {
  const [antiForgeryToken] = useState(getAntiForgeryToken);
  const [elementId] = useState(() => _.uniqueId('PaymentForm'));
  const updatedDetachSubmitEvent = useCallback(
    (...args) => {
      setLoading(false);
      return detachSubmitEvent(...args);
    },
    [detachSubmitEvent, setLoading]
  );

  const {
    paymentContext: {
      PidlConfiguration: {
        MsaDelegateAuthUrl,
        PidlEnabledCountries,
        ExcludedCountriesByCardType,
        PidlErrorFormattingStrings,
        ThreatMetrixUrl,
        PidlSupportedCultureMapping,
        PidlEnvironment,
        PidlScaEmulatorTestHeader,
        MsaDelegateAuthSetDomainEnabled,
        CompanyCodeCurrencyCardTypeMapping,
        CountryCodeCompanyCodeMapping,
      },
      AuthType,
      GreenIdUrl,
      CookieDomain,
      CookiePath,
    },
  } = useContext(PaymentContext);

  const selectedCountryList = useMemo(
    () => _.where(offlinePiCountryList, { Code: country }),
    [offlinePiCountryList, country]
  );

  const deleteCtpPi = useCallback(
    pi => restfulService.post(
      new URI(ccBaseUrl).segment('API/PaymentInstrument/DeleteCtpPI').toString(),
      { cid, ctpPiId: pi.id }
    ),
    [ccBaseUrl, cid, restfulService]
  );
  const ajaxPromise = useMemo(
    () => new AjaxPromise({ restService: restfulService }),
    [restfulService]
  );
  const pifdAuth = useCallback(
    () => PifdAuth(ajaxPromise, ccBaseUrl),
    [ajaxPromise, ccBaseUrl]
  );
  const paasApi = useMemo(
    () => _.mapObject(paymentAsAServiceApi, api => _.partial(api, ajaxPromise)),
    [ajaxPromise]
  );
  const msaAuth = useMemo(
    () => new MsaAuth({
      cookieDomain: CookieDomain,
      cookiePath: CookiePath,
      delegateAuthUrl: MsaDelegateAuthUrl,
      setDomainEnabled: MsaDelegateAuthSetDomainEnabled,
      shouldRetryWithPopup,
      shouldAuthenticateWithPopup,
    }),
    [CookieDomain, CookiePath, MsaDelegateAuthUrl, MsaDelegateAuthSetDomainEnabled, shouldRetryWithPopup, shouldAuthenticateWithPopup]
  );
  const pidlService = useMemo(
    () => new PidlService({
      ccBaseUrl,
      ajaxPromise,
    }),
    [ajaxPromise, ccBaseUrl]
  );

  const offlinePiAddress = useMemo(
    () => (country === address.Country
      ? address
      : {
        Country: country,
      }),
    [address, country]
  );

  const pidlEventHandlers = useEventHandlers(handlePiAddSuccess, setPidlIsValid, clearPiType, setLoading, deleteCtpPi, i18n, setPidlLoading);
  const findCountries = useCallback(() => selectedCountryList, [selectedCountryList]);
  const updatedAttachSubmitEvent = useCallback(
    (event) => {
      attachSubmitEvent((...args) => {
        setLoading(true);
        setPidlIsValid(false);
        event(...args);
      });
    },
    [attachSubmitEvent, setLoading, setPidlIsValid]
  );

  const isPidl = useMemo(
    () => piType !== Constants.PaymentInstrumentType.OfflinePaymentMethod
      && piType !== Constants.PaymentInstrumentType.Unset,
    [piType]
  );

  // if adding the Adder, start loading
  useLayoutEffect(
    () => {
      if (isPidl) {
        setLoading(true);
      }
    },
    [isPidl, setLoading]
  );

  const piData = useMemo(() => ({
    Market: country,
    PiType: getPidlPiType(piType),
    ReturnUrl: returnUrl,
  }), [country, piType, returnUrl]);

  const pidlLogger = useRef();

  if (!isPidl) {
    if (pidlLogger.current) {
      pidlLogger.current.userFail('PidlDisabledForSelection', {
        newCountry: country,
        newPiType: piType,
      });
    }
    pidlLogger.current = undefined;
  } else if (!pidlLogger.current && pidlLoggerCreate) {
    pidlLogger.current = pidlLoggerCreate('SmartAddPIForm', 'Add');

    if (pidlLogger.current) {
      // set options
      pidlLogger.current.setOptions({
        piType,
        scenario: 'SmartAddPiForm',
        renderType: 'create',
        country,
      }).start();
    }
  } else if (pidlLogger.current) {
    // make sure logger options are still correct if the piType/country changes after logger is initialized
    pidlLogger.current.setOptions({
      piType,
      country,
    });
    // For debugging a problem with MCA add payment method
    pidlLogger.current.trace('MCASmartAddPIFormDebugging', {
      cid,
      PiType: getPidlPiType(piType),
      Market: country,
      ReturnUrl: returnUrl,
      country,
      currency,
      address,
      offlinePiCountryList,
    });
  }

  if (piType === Constants.PaymentInstrumentType.Unset) {
    return null;
  } else if (isPidl) {
    return (
      <div className={classnames('pidl-pi-adder', pidlAdderClassName)}>
        <Adder
          cid={cid}
          piData={piData}
          attachSubmitEvent={updatedAttachSubmitEvent}
          detachSubmitEvent={updatedDetachSubmitEvent}
          isPsd2ValidationEnabled={isPsd2ValidationEnabled}
          eventHandlers={pidlEventHandlers}
          i18n={i18n}
          msaAuth={msaAuth}
          pifdAuth={pifdAuth}
          paymentAsAServiceApi={paasApi}
          pidlService={pidlService}
          verificationToken={antiForgeryToken}
          enabledCountries={PidlEnabledCountries}
          excludedCountriesByCard={ExcludedCountriesByCardType}
          pidlErrorFormatMapping={PidlErrorFormattingStrings}
          authenticationType={AuthType}
          ThreatMetrixUrl={ThreatMetrixUrl}
          GreenIdUrl={GreenIdUrl}
          culture={culture}
          supportedCultureMapping={PidlSupportedCultureMapping}
          pidlEnvironment={PidlEnvironment}
          currency={currency}
          challengerTestHeader={PidlScaEmulatorTestHeader}
          getAdditionalSuccessParams={getAdditionalSuccessParams}
          pidlLoggerCreate={pidlLoggerCreate}
          pidlLogger={pidlLogger.current}
          companyCodeCurrencyCardTypeMapping={CompanyCodeCurrencyCardTypeMapping}
          countryCodeCompanyCodeMapping={CountryCodeCompanyCodeMapping}
        />
      </div>
    );
  }

  return (
    <CheckBankTransferForm
      i18n={i18n}
      id={elementId}
      address={offlinePiAddress}
      findStates={findStates}
      findCountries={findCountries}
      onFormValidate={onOfflinePiFormValidate}
      countries={selectedCountryList}
    />
  );
};

RenderAddPIFormUnMemo.displayName = 'RenderAddPIForm';
RenderAddPIFormUnMemo.propTypes = {
  pidlAdderClassName: PropTypes.string,
  cid: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  paymentAdderOptions: PropTypeBaseAddPIForm.isRequired,
  i18n: i18nPropType.isRequired,
  address: addressPropType.isRequired,
  piType: PropTypes.number.isRequired,
  clearPiType: PropTypes.func.isRequired,
  country: PropTypes.string.isRequired,
  offlinePiCountryList: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Code: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
  })).isRequired,
  setPidlIsValid: PropTypes.func.isRequired,
  onOfflinePiFormValidate: PropTypes.func.isRequired,
  shouldRetryWithPopup: PropTypes.bool,
  shouldAuthenticateWithPopup: PropTypes.bool,
};

RenderAddPIFormUnMemo.defaultProps = {
  pidlAdderClassName: '',
  shouldRetryWithPopup: false,
  shouldAuthenticateWithPopup: false,
};

export const RenderAddPIForm = memo(RenderAddPIFormUnMemo);
